import { BooleanInput, coerceBooleanProperty } from "@angular/cdk/coercion";
import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnDestroy, Output } from "@angular/core";
import { ArrayUtils, LocalComponentStore, RxjsUtils } from "@dtm-frontend/shared/utils";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import buffer from "@turf/buffer";
import { Feature, featureCollection as turfFeatureCollection, point as turfPoint } from "@turf/helpers";
import {
    CallbackProperty,
    Cartesian3,
    Math as CesiumMath,
    Color,
    CustomDataSource,
    ImageMaterialProperty,
    PolylineDashMaterialProperty,
    PositionProperty,
} from "cesium";
import { Observable, combineLatest, distinctUntilChanged, map, pairwise, shareReplay, startWith } from "rxjs";
import { CesiumMapService } from "../..";
import {
    CylinderEntity,
    MapEntitiesEditorContent,
    MapEntitiesEditorService,
    MapEntity,
    MapEntityType,
    Polyline3DEntity,
    PrismEntity,
} from "../../services/entity-editors/map-entities-editor.service";
import { areCylinderEntitiesPositionsEqual } from "../../utils/are-cylinder-entities-equal";
import { arePolyline3DEntitiesPositionsEqual } from "../../utils/are-polyline3d-entities-equal";
import { arePrismEntitiesPositionsEqual } from "../../utils/are-prism-entities-equal";
import { convertCylinderEntityToGeoJSONFeature } from "../../utils/convert-cylinder-entity-to-geojson-feature";
import { convertPolyline3DEntityToGeoJSONFeatures } from "../../utils/convert-polyline3d-entity-to-geojson-feature";
import { convertPrismEntityToGeoJSONFeature } from "../../utils/convert-prism-entity-to-geojson-feature";
import { createCesiumDashPattern } from "../../utils/create-cesium-dash-pattern";
import { getSmallestEnclosingCircle } from "../../utils/get-smallest-enclosing-circle";

const DEFAULT_FILL_OPACITY = 0.2;
const DEFAULT_OUTLINE_WIDTH = 2;
const MAX_RADIUS_EPSILON_RATIO = 0.004;
const BOUNDARY_NORMAL_OUTLINE_COLOR = Color.fromCssColorString("#007544"); // $legacy-color-status-success
const BOUNDARY_NORMAL_FILL_COLOR = BOUNDARY_NORMAL_OUTLINE_COLOR.withAlpha(DEFAULT_FILL_OPACITY);
const BOUNDARY_ERROR_OUTLINE_COLOR = Color.fromCssColorString("#ff6f00"); // $legacy-color-primary-900
const BOUNDARY_ERROR_FILL_COLOR = BOUNDARY_ERROR_OUTLINE_COLOR.withAlpha(DEFAULT_FILL_OPACITY);
const DEFAULT_OUTLINE_DASH_LENGTH = 20;
const DEFAULT_OUTLINE_DASH_PATTERN = createCesiumDashPattern("---------  --");
const BOUNDARY_ENTITY_ID = "circular-boundary";
const NO_LIMIT_RADIUS = 40000000;

interface BoundaryEntityOptions {
    position: Cartesian3;
    radius: number;
    outline: Cartesian3[];
    isAreaTooLarge: boolean;
}

class BoundaryEntity implements BoundaryEntityOptions {
    constructor(
        public readonly id: string,
        public position: Cartesian3,
        public radius: number,
        public outline: Cartesian3[],
        public isAreaTooLarge: boolean
    ) {}

    public readonly fillMaterial = new ImageMaterialProperty({
        transparent: false,
        image: "/assets/images/boundary_fill_pattern.png",
        color: new CallbackProperty(() => (this.isAreaTooLarge ? BOUNDARY_ERROR_FILL_COLOR : BOUNDARY_NORMAL_FILL_COLOR), false),
    });
    public readonly outlineMaterial = new PolylineDashMaterialProperty({
        color: new CallbackProperty(() => (this.isAreaTooLarge ? BOUNDARY_ERROR_OUTLINE_COLOR : BOUNDARY_NORMAL_OUTLINE_COLOR), false),
        dashLength: DEFAULT_OUTLINE_DASH_LENGTH,
        dashPattern: DEFAULT_OUTLINE_DASH_PATTERN,
    });

    public get outlineWidth() {
        return DEFAULT_OUTLINE_WIDTH;
    }

    public getCenterCallbackProperty() {
        return new CallbackProperty(() => this.position, false);
    }

    public getOutlineCallbackProperty() {
        return new CallbackProperty(() => this.outline, false);
    }

    public getRadiusCallbackProperty() {
        return new CallbackProperty(() => this.radius, false);
    }

    public update({ position, radius, isAreaTooLarge, outline }: Partial<BoundaryEntityOptions>): void {
        Object.assign(this, { position, radius, outline, isAreaTooLarge });
    }
}

interface CircularBoundaryLayerComponentState {
    isShown: boolean;
    maxRadius: number | undefined;
}

@UntilDestroy()
@Component({
    selector: "dtm-map-lib-circular-boundary-layer[maxRadius]",
    template: "",
    providers: [LocalComponentStore],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CircularBoundaryLayerComponent implements OnDestroy {
    @Input() public set isShown(value: BooleanInput) {
        this.localStore.patchState({ isShown: coerceBooleanProperty(value) });
    }

    @Input() public set maxRadius(value: number | undefined) {
        this.localStore.patchState({ maxRadius: value });
    }

    @Output() protected readonly violationChange = new EventEmitter<boolean>();

    protected readonly isShown$ = this.getIsShownObservable();

    private readonly geometryCachedFeatures = new Map<string, Feature[]>();
    private readonly boundaryEntity$ = this.prepareBoundaryEntity();

    private readonly cesiumViewer = this.cesiumService.getViewer();
    private readonly boundaryDataSource = new CustomDataSource();

    private boundary: BoundaryEntity | undefined;

    constructor(
        private readonly localStore: LocalComponentStore<CircularBoundaryLayerComponentState>,
        private readonly mapEntitiesEditorService: MapEntitiesEditorService,
        private readonly cesiumService: CesiumMapService
    ) {
        this.localStore.setState({
            isShown: true,
            maxRadius: undefined,
        });

        this.watchForGeometryContentChanges();
    }

    public ngOnDestroy(): void {
        this.cesiumViewer.dataSources.remove(this.boundaryDataSource, true);
    }

    private getIsShownObservable(): Observable<boolean> {
        return combineLatest([this.localStore.selectByKey("isShown"), this.localStore.selectByKey("maxRadius")]).pipe(
            map(([isShown, maxRadius]) => isShown && maxRadius !== undefined && maxRadius !== NO_LIMIT_RADIUS),
            distinctUntilChanged()
        );
    }

    private prepareBoundaryEntity(): Observable<BoundaryEntity | undefined> {
        return combineLatest([
            this.mapEntitiesEditorService.editorContent$.pipe(RxjsUtils.filterFalsy(), startWith([]), pairwise()),
            this.localStore.selectByKey("maxRadius").pipe(RxjsUtils.filterFalsy()),
            this.isShown$,
        ]).pipe(
            map(([[previousGeometryContent, geometryContent], maxRadius, isShown]) => {
                if (!isShown) {
                    return undefined;
                }

                const geometry = this.getCombinedGeometry(previousGeometryContent, geometryContent);

                if (!geometry) {
                    return undefined;
                }

                const enclosingCircle = getSmallestEnclosingCircle(geometry);

                if (!enclosingCircle) {
                    return undefined;
                }

                const centerPoint = Cartesian3.fromDegrees(enclosingCircle.centerPoint[0], enclosingCircle.centerPoint[1]);
                const radiusPoint = Cartesian3.fromDegrees(enclosingCircle.radiusPoint[0], enclosingCircle.radiusPoint[1]);
                const radius = Cartesian3.distance(centerPoint, radiusPoint);
                const isAreaTooLarge: boolean = CesiumMath.greaterThan(radius, maxRadius, maxRadius * MAX_RADIUS_EPSILON_RATIO);
                const outline = buffer(turfPoint(enclosingCircle.centerPoint), maxRadius, { units: "meters" });

                return new BoundaryEntity(
                    BOUNDARY_ENTITY_ID,
                    centerPoint,
                    maxRadius,
                    Cartesian3.fromDegreesArray(outline.geometry.coordinates.flat(2)),
                    isAreaTooLarge
                );
            }),
            shareReplay({ refCount: true, bufferSize: 1 })
        );
    }

    private getCombinedGeometry(previousGeometryContent: MapEntitiesEditorContent, geometryContent: MapEntitiesEditorContent) {
        if (this.geometryCachedFeatures.size === 0) {
            previousGeometryContent = [];
        }

        const geometryMap = new Map<string, MapEntity>(geometryContent.map((entity) => [entity.id, entity]));
        const previousGeometryMap = new Map<string, MapEntity>(previousGeometryContent.map((entity) => [entity.id, entity]));

        geometryContent
            .filter((entity) => entity.type === MapEntityType.Polyline3D && Object.keys(entity.childEntities))
            .forEach((entity) => {
                Object.values((entity as Polyline3DEntity).childEntities).forEach((childEntity) => {
                    geometryMap.set(childEntity.entity.id, childEntity.entity);
                });
            });
        previousGeometryContent
            .filter((entity) => entity.type === MapEntityType.Polyline3D && Object.keys(entity.childEntities))
            .forEach((entity) => {
                Object.values((entity as Polyline3DEntity).childEntities).forEach((childEntity) => {
                    previousGeometryMap.set(childEntity.entity.id, childEntity.entity);
                });
            });

        const [existingElements, newElements] = ArrayUtils.partition([...geometryMap.values()], (entity) =>
            previousGeometryMap.has(entity.id)
        );
        const [, removedElements] = ArrayUtils.partition([...previousGeometryMap.values()], (entity) => geometryMap.has(entity.id));

        removedElements.forEach((entity) => this.geometryCachedFeatures.delete(entity.id));
        newElements.forEach((entity) => this.geometryCachedFeatures.set(entity.id, this.convertMapEntityToFeature(entity)));
        existingElements.forEach(({ id }) => {
            const newEntity = geometryMap.get(id);
            const previousEntity = previousGeometryMap.get(id);

            if (!newEntity || !previousEntity || newEntity.type !== previousEntity.type) {
                return;
            }

            let shouldUpdateCachedFeature = false;

            switch (newEntity.type) {
                case MapEntityType.Cylinder:
                    shouldUpdateCachedFeature = !areCylinderEntitiesPositionsEqual(newEntity, previousEntity as CylinderEntity);
                    break;
                case MapEntityType.Prism:
                    shouldUpdateCachedFeature = !arePrismEntitiesPositionsEqual(newEntity, previousEntity as PrismEntity);
                    break;
                case MapEntityType.Polyline3D:
                    shouldUpdateCachedFeature = !arePolyline3DEntitiesPositionsEqual(newEntity, previousEntity as Polyline3DEntity);
                    break;
            }

            if (shouldUpdateCachedFeature) {
                this.geometryCachedFeatures.set(newEntity.id, this.convertMapEntityToFeature(newEntity));
            }
        });

        const cachedFeatures = Array.from(this.geometryCachedFeatures.values()).flat();

        if (cachedFeatures.length === 0) {
            return undefined;
        }

        return turfFeatureCollection(cachedFeatures);
    }

    private convertMapEntityToFeature(entity: MapEntity): Feature[] {
        switch (entity.type) {
            case MapEntityType.Cylinder:
                return [convertCylinderEntityToGeoJSONFeature(entity)];
            case MapEntityType.Prism:
                return [convertPrismEntityToGeoJSONFeature(entity)];
            case MapEntityType.Polyline3D:
                return convertPolyline3DEntityToGeoJSONFeatures(entity).features;
        }
    }

    private watchForGeometryContentChanges() {
        this.cesiumViewer.dataSources.add(this.boundaryDataSource);

        this.boundaryEntity$.pipe(untilDestroyed(this)).subscribe((boundary) => {
            if (!boundary) {
                this.boundaryDataSource.entities.removeAll();
                this.boundary = undefined;

                return;
            }

            if (this.boundary) {
                this.boundary.update(boundary);
            } else {
                this.boundary = boundary;
            }

            if (!this.boundaryDataSource.entities.getById(BOUNDARY_ENTITY_ID)) {
                this.boundaryDataSource.entities.add({
                    id: BOUNDARY_ENTITY_ID,
                    position: this.boundary.getCenterCallbackProperty() as unknown as PositionProperty,
                    ellipse: {
                        semiMinorAxis: this.boundary.getRadiusCallbackProperty(),
                        semiMajorAxis: this.boundary.getRadiusCallbackProperty(),
                        granularity: 0.01,
                        material: this.boundary.fillMaterial,
                    },
                    polyline: {
                        positions: this.boundary.getOutlineCallbackProperty(),
                        material: this.boundary.outlineMaterial,
                        width: this.boundary.outlineWidth,
                    },
                });
            }

            this.cesiumService.getScene().requestRender();
        });

        this.boundaryEntity$
            .pipe(
                map((entity) => !!entity?.isAreaTooLarge),
                distinctUntilChanged(),
                untilDestroyed(this)
            )
            .subscribe((isAreaTooLarge: boolean) => this.violationChange.emit(!isAreaTooLarge));
    }
}
